import { useCallback } from 'react';
import axios from 'axios';

export const useAdminLoaders = ({
  BACKEND_URL,
  adminCacheRef,
  debouncedAdminUserSearch,
  adminUserTypeFilter,
  adminUserBlockedFilter,
  setAdminUsers,
  debouncedAdminReviewFilters,
  setAdminReviews,
  setAdminReviewsLoading,
  setAdminBarbershops,
  adminMetricsStart,
  adminMetricsEnd,
  setAdminMetrics,
  setAdminAuditLogs,
  setAdminNotifications,
  setMaintenanceEnabled,
  setMaintenanceMessage,
  setAdminUserStats,
  adminVisitsStart,
  adminVisitsEnd,
  setAdminVisitStats,
  setAdminVisitEvents
}) => {
  const loadAdminUsers = useCallback(async () => {
    try {
      const cacheKey = JSON.stringify({
        q: debouncedAdminUserSearch || '',
        type: adminUserTypeFilter,
        blocked: adminUserBlockedFilter
      });
      const cached = adminCacheRef.current.users.get(cacheKey);
      if (cached && Date.now() - cached.ts < 20000) {
        setAdminUsers(cached.data);
        return;
      }
      const token = localStorage.getItem('token');
      const params = {};
      if (debouncedAdminUserSearch) params.q = debouncedAdminUserSearch;
      if (adminUserTypeFilter !== 'all') params.user_type = adminUserTypeFilter;
      if (adminUserBlockedFilter !== 'all') params.blocked = adminUserBlockedFilter === 'true';
      const response = await axios.get(`${BACKEND_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      const users = response.data.users || [];
      setAdminUsers(users);
      adminCacheRef.current.users.set(cacheKey, { ts: Date.now(), data: users });
    } catch (error) {
      console.error('Error loading admin users:', error);
    }
  }, [
    BACKEND_URL,
    adminCacheRef,
    adminUserBlockedFilter,
    adminUserTypeFilter,
    debouncedAdminUserSearch,
    setAdminUsers
  ]);

  const loadAdminReviews = useCallback(async () => {
    try {
      setAdminReviewsLoading(true);
      const cacheKey = JSON.stringify({
        start: debouncedAdminReviewFilters.startDate || '',
        end: debouncedAdminReviewFilters.endDate || '',
        minRating: debouncedAdminReviewFilters.minRating || '',
        maxRating: debouncedAdminReviewFilters.maxRating || '',
        minPrice: debouncedAdminReviewFilters.minPrice || '',
        maxPrice: debouncedAdminReviewFilters.maxPrice || '',
        barber: debouncedAdminReviewFilters.barberId || '',
        barbershop: debouncedAdminReviewFilters.barbershopId || '',
        flagged: debouncedAdminReviewFilters.flaggedOnly || false
      });
      const cached = adminCacheRef.current.reviews.get(cacheKey);
      if (cached && Date.now() - cached.ts < 20000) {
        setAdminReviews(cached.data);
        return;
      }
      const token = localStorage.getItem('token');
      const params = {};
      if (debouncedAdminReviewFilters.startDate) params.start_date = debouncedAdminReviewFilters.startDate;
      if (debouncedAdminReviewFilters.endDate) params.end_date = debouncedAdminReviewFilters.endDate;
      if (debouncedAdminReviewFilters.minRating) params.min_rating = Number(debouncedAdminReviewFilters.minRating);
      if (debouncedAdminReviewFilters.maxRating) params.max_rating = Number(debouncedAdminReviewFilters.maxRating);
      if (debouncedAdminReviewFilters.minPrice) params.min_price = Number(debouncedAdminReviewFilters.minPrice);
      if (debouncedAdminReviewFilters.maxPrice) params.max_price = Number(debouncedAdminReviewFilters.maxPrice);
      if (debouncedAdminReviewFilters.barberId && debouncedAdminReviewFilters.barberId !== 'all') {
        params.barber_id = debouncedAdminReviewFilters.barberId;
      }
      if (debouncedAdminReviewFilters.barbershopId && debouncedAdminReviewFilters.barbershopId !== 'all') {
        params.barbershop_id = debouncedAdminReviewFilters.barbershopId;
      }
      if (debouncedAdminReviewFilters.flaggedOnly) params.flagged = true;
      const response = await axios.get(`${BACKEND_URL}/api/admin/reviews`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      const reviews = response.data.reviews || [];
      setAdminReviews(reviews);
      adminCacheRef.current.reviews.set(cacheKey, { ts: Date.now(), data: reviews });
    } catch (error) {
      console.error('Error loading admin reviews:', error);
    } finally {
      setAdminReviewsLoading(false);
    }
  }, [
    BACKEND_URL,
    adminCacheRef,
    debouncedAdminReviewFilters,
    setAdminReviews,
    setAdminReviewsLoading
  ]);

  const loadAdminBarbershops = useCallback(async () => {
    try {
      const cached = adminCacheRef.current.barbershops.get('all');
      if (cached && Date.now() - cached.ts < 20000) {
        setAdminBarbershops(cached.data);
        return;
      }
      const response = await axios.get(`${BACKEND_URL}/api/barbershops`);
      const barbershops = response.data.barbershops || [];
      setAdminBarbershops(barbershops);
      adminCacheRef.current.barbershops.set('all', { ts: Date.now(), data: barbershops });
    } catch (error) {
      console.error('Error loading admin barbershops:', error);
    }
  }, [BACKEND_URL, adminCacheRef, setAdminBarbershops]);

  const loadAdminMetrics = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (adminMetricsStart) params.start_date = adminMetricsStart;
      if (adminMetricsEnd) params.end_date = adminMetricsEnd;
      const response = await axios.get(`${BACKEND_URL}/api/admin/metrics`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setAdminMetrics(response.data);
    } catch (error) {
      console.error('Error loading admin metrics:', error);
    }
  }, [BACKEND_URL, adminMetricsEnd, adminMetricsStart, setAdminMetrics]);

  const loadAdminAuditLogs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/audit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminAuditLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error loading audit logs:', error);
    }
  }, [BACKEND_URL, setAdminAuditLogs]);

  const loadAdminNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error loading admin notifications:', error);
    }
  }, [BACKEND_URL, setAdminNotifications]);

  const loadAdminMaintenance = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/settings/maintenance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMaintenanceEnabled(Boolean(response.data.enabled));
      setMaintenanceMessage(response.data.message || '');
    } catch (error) {
      console.error('Error loading maintenance:', error);
    }
  }, [BACKEND_URL, setMaintenanceEnabled, setMaintenanceMessage]);

  const loadAdminUserStats = useCallback(async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/clients/${userId}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminUserStats((prev) => ({ ...prev, [userId]: response.data }));
    } catch (error) {
      console.error('Error loading user stats:', error);
    }
  }, [BACKEND_URL, setAdminUserStats]);

  const loadAdminVisitStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (adminVisitsStart) params.start_date = adminVisitsStart;
      if (adminVisitsEnd) params.end_date = adminVisitsEnd;
      const response = await axios.get(`${BACKEND_URL}/api/admin/visit-stats`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setAdminVisitStats(response.data);
    } catch (error) {
      console.error('Error loading visit stats:', error);
    }
  }, [BACKEND_URL, adminVisitsStart, adminVisitsEnd, setAdminVisitStats]);

  const loadAdminVisitEvents = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = { limit: 500 };
      if (adminVisitsStart) params.start_date = adminVisitsStart;
      if (adminVisitsEnd) params.end_date = adminVisitsEnd;
      const response = await axios.get(`${BACKEND_URL}/api/admin/visit-events`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setAdminVisitEvents(response.data.events || []);
    } catch (error) {
      console.error('Error loading visit events:', error);
    }
  }, [BACKEND_URL, adminVisitsStart, adminVisitsEnd, setAdminVisitEvents]);

  return {
    loadAdminUsers,
    loadAdminReviews,
    loadAdminBarbershops,
    loadAdminMetrics,
    loadAdminAuditLogs,
    loadAdminNotifications,
    loadAdminMaintenance,
    loadAdminUserStats,
    loadAdminVisitStats,
    loadAdminVisitEvents
  };
};
